import { useState, useEffect } from 'react';
import HeroBanner from './components/HeroBanner';
import Navigation from './components/Navigation';
import AuthorBio from './components/AuthorBio';
import ReviewHighlights from './components/ReviewHighlights';
import PurchaseSection from './components/PurchaseSection';
import Footer from './components/Footer';

const SCHEMA_SCRIPT_ID = 'home-page-schema-ld';

export default function Home() {
  const [scrolled, setScrolled] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const siteUrl = (import.meta.env.VITE_SITE_URL as string) || '';

    const schemaGraph = {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'WebSite',
          '@id': `${siteUrl}/#website`,
          name: 'The Forgotten Felon Blueprint',
          url: `${siteUrl}/`,
        },
        {
          '@type': 'WebPage',
          '@id': `${siteUrl}/#webpage`,
          url: `${siteUrl}/`,
          name: 'The Forgotten Felon - John Reed | Reentry Survival Guide',
          description:
            "Discover 'The Forgotten Felon Blueprint' by John Reed — a raw, unfiltered reentry survival guide for formerly incarcerated individuals ready to reclaim their freedom.",
          isPartOf: { '@id': `${siteUrl}/#website` },
          author: { '@id': `${siteUrl}/#person-john-reed` },
          inLanguage: 'en-US',
        },
        {
          '@type': 'Person',
          '@id': `${siteUrl}/#person-john-reed`,
          name: 'John Reed',
          jobTitle: 'Author, Speaker, Reentry Advocate',
          description:
            'John Reed is a reentry advocate, speaker, and author of The Forgotten Felon Blueprint. He speaks at reentry programs, correctional facilities, and community events across the country.',
          image:
            'https://static.readdy.ai/image/b0bf0689308dc3dbfe962826cee4efbe/6d2ceda2edb36098947cfc1fcde7ae02.png',
          url: `${siteUrl}/`,
        },
        {
          '@type': 'Book',
          '@id': `${siteUrl}/#book-survival-guide`,
          name: 'The Forgotten Felon Blueprint — Survival Guide',
          author: { '@id': `${siteUrl}/#person-john-reed` },
          url: `${siteUrl}/`,
          image:
            'https://static.readdy.ai/image/b0bf0689308dc3dbfe962826cee4efbe/95e5927307d061922e242261363b9b2d.jpeg',
          description:
            'A raw, unfiltered reentry survival guide built from personal pain, setbacks, and hard-won breakthroughs. Written for formerly incarcerated individuals navigating a world that was not built for them.',
          inLanguage: 'en-US',
          genre: 'Self-Help / Reentry',
          offers: {
            '@type': 'Offer',
            price: '14.99',
            priceCurrency: 'USD',
            availability: 'https://schema.org/InStock',
            url: 'https://www.amazon.com/FORGOTTEN-FELON-BLUEPRINT-SURVIVAL-GUIDE/dp/B0GQQ2GD8L/',
            seller: {
              '@type': 'Organization',
              name: 'Amazon',
            },
          },
        },
        {
          '@type': 'Book',
          '@id': `${siteUrl}/#book-workbook`,
          name: 'The Forgotten Felon — Workbook',
          author: { '@id': `${siteUrl}/#person-john-reed` },
          url: `${siteUrl}/`,
          image:
            'https://static.readdy.ai/image/b0bf0689308dc3dbfe962826cee4efbe/2e6f5304253a2f72a1e4ba066b3987d8.jpeg',
          description:
            'The companion workbook to The Forgotten Felon Blueprint — packed with exercises, action plans, and accountability tools to put the blueprint into practice.',
          inLanguage: 'en-US',
          genre: 'Self-Help / Reentry',
          offers: {
            '@type': 'Offer',
            price: '16.99',
            priceCurrency: 'USD',
            availability: 'https://schema.org/InStock',
            url: 'https://www.amazon.com/dp/B0GSR1TWHT/',
            seller: {
              '@type': 'Organization',
              name: 'Amazon',
            },
          },
        },
        {
          '@type': 'Review',
          '@id': `${siteUrl}/#review-janaei`,
          itemReviewed: { '@id': `${siteUrl}/#book-survival-guide` },
          author: {
            '@type': 'Person',
            name: 'Janaei',
          },
          reviewRating: {
            '@type': 'Rating',
            ratingValue: '5',
            bestRating: '5',
          },
          reviewBody:
            "MUST READ!!! I initially purchased this book for someone I know who's currently incarcerated but I couldn't help but read it myself after the first few pages. I believe this material should be in prisons all over the world to motivate and give felons hope!!",
          publisher: {
            '@type': 'Organization',
            name: 'Amazon',
          },
        },
      ],
    };

    const existing = document.getElementById(SCHEMA_SCRIPT_ID);
    if (existing) existing.remove();

    const script = document.createElement('script');
    script.id = SCHEMA_SCRIPT_ID;
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(schemaGraph);
    document.head.appendChild(script);

    return () => {
      const s = document.getElementById(SCHEMA_SCRIPT_ID);
      if (s) s.remove();
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
      setShowScrollTop(window.scrollY > 400);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="relative">
      <Navigation scrolled={scrolled} />
      <HeroBanner />
      <AuthorBio />
      <ReviewHighlights />
      <PurchaseSection />
      <Footer />

      {/* Scroll to Top Button */}
      <button
        onClick={scrollToTop}
        className={`fixed bottom-8 right-8 z-50 w-12 h-12 rounded-full bg-amber-700 text-white flex items-center justify-center shadow-md transition-all duration-300 cursor-pointer hover:bg-amber-600 ${
          showScrollTop ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'
        }`}
        aria-label="Scroll to top"
      >
        <i className="ri-arrow-up-line text-xl"></i>
      </button>
    </div>
  );
}
